/**
 * Shared "why is this not eligible" pop-up. Hover tooltips cannot hold a criteria table,
 * so every status badge opens this instead.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'antd';
import { MdClose } from 'react-icons/md';
import { EligibilityReasonTable } from './EligibilityReasonTable';
import type { EligibilityReasonRow } from '../../../../services/financeEligibilityService';

export type EligibilityDetails = {
    heading: string;
    rows?: EligibilityReasonRow[];
    reason?: string;
};

/** Lets any status badge open the shared details pop-up without prop drilling. */
export const EligibilityDetailsContext = React.createContext<
    ((details: EligibilityDetails) => void) | null
>(null);

export function EligibilityDetailsModal({
    details,
    onClose,
}: {
    details: EligibilityDetails;
    onClose: () => void;
}) {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return (
        <div className="fp-eligibility-details">
            <button
                type="button"
                className="fp-eligibility-details__backdrop"
                aria-label="Close eligibility details"
                onClick={onClose}
            />
            <div
                className="fp-eligibility-details__panel"
                role="dialog"
                aria-modal
                aria-label={`${details.heading} details`}
            >
                <div className="fp-eligibility-details__header">
                    <span className="fp-eligibility-details__title">{details.heading}</span>
                    <Button type="text" size="small" onClick={onClose} aria-label="Close">
                        <MdClose />
                    </Button>
                </div>
                <div className="fp-eligibility-details__body">
                    {details.rows?.length ? (
                        <EligibilityReasonTable heading="Criteria results" rows={details.rows} />
                    ) : (
                        <div className="fp-eligibility__info-notes">
                            {details.reason || 'No criteria details available.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Wraps children in the badge context and renders the pop-up when one is opened.
 * The pop-up goes to `body` so an antd Modal host cannot clip or under-stack it.
 */
export const EligibilityDetailsHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [details, setDetails] = useState<EligibilityDetails | null>(null);
    const close = useCallback(() => setDetails(null), []);
    return (
        <EligibilityDetailsContext.Provider value={setDetails}>
            {children}
            {details
                ? createPortal(
                      <EligibilityDetailsModal details={details} onClose={close} />,
                      document.body,
                  )
                : null}
        </EligibilityDetailsContext.Provider>
    );
};
